import type {CSSInterpolation, Theme} from '@mui/material/styles';

/**
 * Global styles for the work/expression column `<ViewTransition>`s in the
 * metadata editor. View transition pseudo-elements live on the document root
 * rather than on any component, so they cannot be styled with `sx`.
 *
 * The classes are set on the `<ViewTransition>` boundaries in WorkScene, one
 * per animation type: a column that resizes is animated differently from one
 * that is added or removed.
 */
const columns = ['work', 'expression'] as const;
const animations = ['enter', 'exit', 'resize'] as const;

const snapshotsOf = (column: (typeof columns)[number]) =>
  animations
    .flatMap(animation => [
      `::view-transition-old(.${column}-${animation})`,
      `::view-transition-new(.${column}-${animation})`,
    ])
    .join(', ');

const groups = columns
  .flatMap(column =>
    animations.map(
      animation => `::view-transition-group(.${column}-${animation})`,
    ),
  )
  .join(', ');

const clippedToColumn = (anchor: 'left' | 'right'): CSSInterpolation => ({
  height: '100%',
  objectFit: 'none',
  objectPosition: `top ${anchor}`,
});

export const viewTransitionStyles = (theme: Theme): CSSInterpolation => {
  const timing = {
    animationDuration: `${theme.transitions.duration.standard}ms`,
    animationTimingFunction: theme.transitions.easing.easeInOut,
  };

  return {
    [groups]: timing,
    [snapshotsOf('work')]: clippedToColumn('left'),
    [snapshotsOf('expression')]: clippedToColumn('right'),

    // A column that is added or removed keeps its size — there is no box to
    // morph — so it slides in and out of its own side of the row instead.
    [[
      '::view-transition-new(.work-enter)',
      '::view-transition-old(.work-exit)',
      '::view-transition-new(.expression-enter)',
      '::view-transition-old(.expression-exit)',
    ].join(', ')]: {...timing, animationFillMode: 'both'},

    '::view-transition-new(.work-enter)': {
      animationName: 'column-in-from-left',
    },
    '::view-transition-old(.work-exit)': {
      animationName: 'column-out-to-left',
    },
    '::view-transition-new(.expression-enter)': {
      animationName: 'column-in-from-right',
    },
    '::view-transition-old(.expression-exit)': {
      animationName: 'column-out-to-right',
    },

    '@keyframes column-in-from-left': {
      from: {transform: 'translateX(-100%)', opacity: 0},
    },
    '@keyframes column-out-to-left': {
      to: {transform: 'translateX(-100%)', opacity: 0},
    },
    '@keyframes column-in-from-right': {
      from: {transform: 'translateX(100%)', opacity: 0},
    },
    '@keyframes column-out-to-right': {
      to: {transform: 'translateX(100%)', opacity: 0},
    },
  };
};
